import { GridRow } from '../../../../../interfaces/grids/GridRow'
import { AccountInfo } from '../AccountFilter'

/**
 * Extracts unique accounts from cashflow data
 * Returns a list of AccountInfo objects sorted by grandParent -> parent -> account
 */
export const extractAccountsFromData = (
  data: GridRow[] | undefined
): AccountInfo[] => {
  if (!data) return []

  const accountMap = new Map<number, AccountInfo>()

  data.forEach((row) => {
    const accountId = row.accountId as number | undefined
    const accountNumber = row.accountNumber as string | undefined
    const displayName = row.displayName as string | undefined
    const parentDisplayName = row.parentDisplayName as string | undefined
    const grandParentDisplayName = row.grandParentDisplayName as
      | string
      | undefined
    const grandParentSortOrder = row.grandParentSortOrder as number | undefined
    const parentSortOrder = row.parentSortOrder as number | undefined
    const sortOrder = row.sortOrder as number | undefined
    const comment = row.comment as string | null | undefined
    // Check difference first, then prognosisDifference as fallback for compatibility
    const difference = (row.difference ?? row.prognosisDifference) as
      | number
      | null
      | undefined

    // Only rows with accountId and accountNumber are actual accounts (not group rows)
    if (accountId && accountNumber && displayName) {
      if (!accountMap.has(accountId)) {
        accountMap.set(accountId, {
          accountId,
          accountNumber,
          displayName,
          parentDisplayName: parentDisplayName || '',
          grandParentDisplayName: grandParentDisplayName || '',
          grandParentSortOrder,
          parentSortOrder,
          sortOrder,
          difference: difference ?? null,
          comment: comment ?? null,
        })
      }
    }
  })

  // Sort by same logic as grid: grandParentSortOrder -> parentSortOrder -> sortOrder
  const accounts = Array.from(accountMap.values())
  accounts.sort((a, b) => {
    // First sort by grandParentSortOrder (same as grid)
    const gpSortA = a.grandParentSortOrder ?? 0
    const gpSortB = b.grandParentSortOrder ?? 0
    if (gpSortA !== gpSortB) return gpSortA - gpSortB

    // Then by parentSortOrder
    const pSortA = a.parentSortOrder ?? 0
    const pSortB = b.parentSortOrder ?? 0
    if (pSortA !== pSortB) return pSortA - pSortB

    // Finally by sortOrder
    const sortA = a.sortOrder ?? 0
    const sortB = b.sortOrder ?? 0
    if (sortA !== sortB) return sortA - sortB

    // Fallback to sorting by account number if all sort orders are equal or missing
    return a.accountNumber.localeCompare(b.accountNumber, 'sv', {
      numeric: true,
    })
  })

  return accounts
}
